import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Messaging, getToken, onMessage } from '@angular/fire/messaging';
import { environment } from '../../../environments/environment';
import { Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';

import { TokenService } from './token.service';

@Injectable({ providedIn: 'root' })
export class NotificationService {

  private readonly usersUrl = environment.usersUrl;
  private isListening = false;
  private isSendingToken = false;

  // 🔔 Global notification stream
  public notification$ = new BehaviorSubject<any>(null);
  private handledMessages = new Set<string>();

  constructor(
    private messaging: Messaging,
    private http: HttpClient,
    private router: Router,
    private tokenService: TokenService
  ) { }
  async requestPermission(): Promise<string | null> {
    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        // console.log('❌ Notification permission denied');
        return null;
      }
      const registration = await navigator.serviceWorker.ready;
      const token = await getToken(this.messaging, {
        vapidKey: environment.firebase.vapidKey,
        serviceWorkerRegistration: registration
      });
      if (!token) {
        // console.log("❌ No FCM token received");
        return null;
      }

      localStorage.setItem('fcmToken', token);

      // ✅ Send to backend if logged in
      if (this.tokenService.isLoggedIn()) {
        this.sendTokenToBackend(token);
      }

      return token;
    } catch (error) {
      console.error('❌ FCM error:', error);
      return null;
    }
  }
  /**
   * 📡 Send token to backend
   */
  sendTokenToBackend(token?: string, force = false) {
    const fcmToken = token || localStorage.getItem('fcmToken');
    if (!fcmToken || !this.tokenService.isLoggedIn()) {
      return;
    }

    const syncKey = `fcm_synced_${fcmToken}`;
    if (!force && (this.isSendingToken || localStorage.getItem(syncKey) === 'true')) {
      return;
    }

    this.isSendingToken = true;

    this.http.post(`${this.usersUrl}/fcm-token`, { fcmToken })
      .subscribe({
        next: () => {
          this.isSendingToken = false;
          localStorage.setItem(syncKey, 'true');
        },
        error: (err) => {
          this.isSendingToken = false;
          console.error('❌ Token save failed', err);
        }
      });
  }

  /**
   * 📩 Foreground messages
   */
  listen() {
    if (this.isListening) return;
    this.isListening = true;

    // console.log('👂 Listening (FCM + SW)...');

    const handle = (payload: any) => {
      const id = payload?.messageId || payload?.fcmMessageId;


      if (!id) return;

      if (this.handledMessages.has(id)) return; // 🛑 STOP DUPLICATE
      this.handledMessages.add(id);


      // console.log('✅ FINAL:', payload);

      this.notification$.next(payload);
      this.showBrowserNotification(payload);
    };

    // 🔥 FOREGROUND
    onMessage(this.messaging, handle);

    // 🔥 BACKGROUND (SW)
    navigator.serviceWorker.addEventListener('message', (event: any) => {
      handle(event.data);
    });
  }
  private showBrowserNotification(payload: any) {
    if (Notification.permission !== 'granted') return;

    const title =
      payload?.notification?.title ||
      payload?.data?.title ||
      'SafeIgnite';

    const body =
      payload?.notification?.body ||
      payload?.data?.body ||
      '';

    const link =
      payload?.data?.link ||
      payload?.fcmOptions?.link ||
      '/';

    const notification = new Notification(title, {
      body,
      // icon: '/assets/images/logo.png' // optional
    });

    // 🔥 click action
    notification.onclick = () => {
      window.focus();
      this.router.navigateByUrl(link);
    };
  }
}

